import { Injectable } from "@angular/core";
import { TVDMetricGroupResponse, TvdMetricData, TvdMetricGroup, availableBrandGroups, eroiIds, tvdGroupIds } from "../types/tvd-types";
import { TVDModel } from "../types/tvd-model";
import { TvdRequests } from "src/app/core/services/tvd-requests.service";
import { AlertService } from "src/app/core/services/alert.service";
import { Guid } from "guid-typescript";
import { FiltersService } from "src/app/core/services/filters.service";
import { ContractModel } from "src/app/global/contract-filter-model";
import { BehaviorSubject } from "rxjs/internal/BehaviorSubject";
import { cloneDeep } from "lodash";

@Injectable({
    providedIn: 'root'
})
export class TVDDistributorService {
  filters: ContractModel | undefined;

  // Response from TVD metrics endpoint
  response: TVDMetricGroupResponse[] = [];

  // Response formatted for FE manipulation and consumption
  tvdMetricGroups: TvdMetricGroup[] = [];

  //model of metrics added to the presentation
  presentationMetrics: TVDMetricGroupResponse[] = [];

  // TVD model Observable
  private tvdSource = new BehaviorSubject<TVDModel | undefined>(undefined);
  tvdObservable = this.tvdSource.asObservable();

  // holds selected Filter Options, so that Distributor Group can be filtered by brand name(s) 
  private defaultSelectedFilterOptions: { [id: string]: string[] } = {
    [availableBrandGroups.ALL]: [],
    [availableBrandGroups.ECOLAB]: [],
    [availableBrandGroups.DISTRIBUTOR]: []
  };
  private distributorSelectedFilterOptions = new BehaviorSubject<{ [id: string]: string[] }>(this.defaultSelectedFilterOptions);
  
  //holds the selected Brand Group (tabs in the UI) for Distributor TVD
  private selectedBrandGroup = new BehaviorSubject<availableBrandGroups>(availableBrandGroups.ALL);

  constructor(
      private tvdRequests : TvdRequests,
      private alertService : AlertService,
      private filterService : FiltersService
  ) {
    this.filterService.selectedFiltersObservable.subscribe(f => this.filters = f);
  }

  /**
   * Retrieves Product and Business Metrics response
   * @returns Product and Business Metrics response
   */
  async getTVDData() {
    return await this.tvdRequests.getTotalValueDelivered().then(response => {
      const { data } = response;
      if (data != undefined) {
        return data;
      }
      }).catch(error => {
        this._handleError('Retrieving Distributor TVD data failed.', error);
      });
  }

  /**
   * Handles any error (can be moved since it is generic)
   * @param errorMessage 
   * @param error 
   */
  private _handleError(errorMessage: string, error: any) {
    console.log(errorMessage + ":" + JSON.stringify(error));
    this.alertService.addAlert({
    message: `${errorMessage} If problem persists, please report a bug.`,
    id: Guid.create()
    });
  }

  /**
   * Retrieves response, sets presentation metrics to default (all selected), and parses response into metric groups
   */
  async setDefaultTVDModel(){
    this.response = await this.getTVDData();

    // Set presentation metrics to default (all selected)
    this.presentationMetrics = cloneDeep(this.response);

    // Set the selected brands and brand group to the default
    this.setSelectedFilterOptions(this.defaultSelectedFilterOptions);
    this.setSelectedBrandGroup(availableBrandGroups.ALL);

    // Parse response into type to display in UI
    this.parseResponseToTvdMetricGroups(this.response);

    // Update observable
    this.confirmModelUpdate();
  }

  /**
   * Reset TVD observable to default state
   */
  resetTVDModel() {
    // reset properties in model
    this.response = [];
    this.presentationMetrics = [];
    this.tvdMetricGroups = [];
    this.setSelectedFilterOptions(this.defaultSelectedFilterOptions);
    this.setSelectedBrandGroup(availableBrandGroups.ALL);

    // Update observable
    this.confirmModelUpdate();
  }

  /**
   * Trigger change in TVD observable
   */
  confirmModelUpdate(){
    let newModel = new TVDModel(
      this.response,
      this.presentationMetrics,
      this.tvdMetricGroups,
      );

    this.tvdSource.next(newModel);
  }

  setSelectedFilterOptions(metrics: { [id: string]: string[] }) {
    this.distributorSelectedFilterOptions.next(metrics);
  }
  getSelectedFilterOptions() : { [id: string]: string[] } {
    return cloneDeep(this.distributorSelectedFilterOptions.getValue());
  }

  setSelectedBrandGroup(brandGroup:availableBrandGroups) {
    this.selectedBrandGroup.next(brandGroup);
  }
  getSelectedBrandGroup() : availableBrandGroups {
    return cloneDeep(this.selectedBrandGroup.getValue());
  }

  /**
   * Parses response into a metric groups for easier FE consumption
   * @param response response from TVD metrics endpoint
   * @returns response in the TvdMetricGroup format
   */
  parseResponseToTvdMetricGroups(response: TVDMetricGroupResponse[]) {
    this.tvdMetricGroups = response.map(responseGroup => {
      // Initialize UI type for current metric group
      let metricGroup: TvdMetricGroup = {
        id: responseGroup.id,
        title: this.getMetricGroupTitle(responseGroup.id),
        brandGroup: responseGroup.brandGroup,
        groupSelected: false, //default metric group selections to false
        indvMetrics: [],
        summMetrics: [...responseGroup.summMetrics] // no parsing needed for summary metrics
      };
      // Populate metrics for the group
      responseGroup.indvMetrics.forEach(metric => {
        if(metric.id === eroiIds.TOTALSALES) //Total Sales is not a metric to be displayed in the UI
          return;
        //handle special case where Assets tile is sum of replacement and repair metrics
        else if(metric.id === "replacement" || metric.id == "repair") { 
          let assetMetric = metricGroup.indvMetrics.find(metric => metric.id === eroiIds.ASSETS);

          if(assetMetric){ //asset metric already created, add to it
            assetMetric.monetaryContribution = (parseFloat(assetMetric.monetaryContribution?.replace(/,/g, '') ?? '0') 
              + parseFloat(metric.monetaryContribution?.replace(/,/g, '') ?? '0')).toString();
          } else { //create asset metric
            let assetData = this.makeMetricData(eroiIds.ASSETS, metric['$type'], metric.brand,metric.sortSequenceNumber, undefined, metric.monetaryContribution, metric.currentValue, metric.previousValue);
            metricGroup.indvMetrics.push(assetData);
          }
        } else { //Regular TVD metric
          let metricData = this.makeMetricData(metric.id, metric.$type, metric.brand, metric.sortSequenceNumber, metric.valueContribution, metric.monetaryContribution, metric.currentValue, metric.previousValue);
          metricGroup.indvMetrics.push(metricData);
        }
      });
      return metricGroup;
    });
  }


  /**
   * Updates presentation metrics using the selected metrics
   * @param metricGroups FE representation of metric groups, including user selection
   */
  updatePresentationMetrics(metricGroups: TvdMetricGroup[]) {
    metricGroups.forEach(group => {
      let presentationGroup = this.presentationMetrics.find(presentationGroup => presentationGroup.id === group.id && presentationGroup.brandGroup === group.brandGroup);

      if(presentationGroup){
        // Update metrics for presentation based on selection
        if(group.groupSelected) { // Should include group's individual metrics
          // Use original response to get individual metrics
          let responseGroup = this.response.find(responseGroup => responseGroup.id === group.id && responseGroup.brandGroup === group.brandGroup);
          presentationGroup.indvMetrics = [...responseGroup?.indvMetrics ?? []];
        } else { // Shouldn't include group's individual metrics
          presentationGroup.indvMetrics = [];
        }
      } else {
        throw new Error(`updatePresentationMetrics: Could not find metric group with id: ${group.id}`);
      }
    });

    // Trigger change to observable
    this.confirmModelUpdate();
  }

  /**
   * Makes a TVD metric with given params
   * @param id unique id of the metric
   * @param valueContribution main display value of the metric
   * @param monetaryContribution secondary value of the metric 
   * @returns TVD metric typed to TvdMetricData
   */
  makeMetricData(
    id : string,
    type: 'TvdCostMetric' | 'TvdGrowthMetric',
    brand: string | undefined,
    sortSequenceNumber: number,
    valueContribution: string | undefined,
    monetaryContribution: string | undefined,
    currentValue: number | undefined,
    previousValue: number | undefined
  ) : TvdMetricData {
    return {
        id: id,
        '$type': type,
        brand: brand,
        sortSequenceNumber: sortSequenceNumber,
        valueContribution: valueContribution ?? '0',
        monetaryContribution: monetaryContribution ?? '',
        currentValue: currentValue,
        previousValue: previousValue
    }
  }

  /**
   * Gets the title of the metric group based on the group ID. 
   * In future refactor this can be moved to metric group component level
   * @param groupID unique ID of the metric group
   * @returns title of the group to display in the UI
   */
  getMetricGroupTitle(groupID: string) {
    switch (groupID) {
      case tvdGroupIds.DEFAULT:
        return "Product Metrics";
      case tvdGroupIds.DISTRIBUTOR:
        return "Business Metrics";
      default:
        throw new Error(`Unknown metric group ID: ${groupID}`);
    }
  }
}